import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import { useExtensionUpsell } from "@mirage/shared/util/webExtensionsHelpers";
import i18n from "@mirage/translations";
import { useEffect, useState } from "react";
import { recentActivities, recentBrowserHistory, recentEntities } from "..";

import type { RecentData } from "../service";
import type {
  RecentActivitiesContent,
  RecentBrowserContent,
  RecentConnectorContent,
  RecentUpsellContent,
} from "../types";

export const useRecentBrowserHistory = (): RecentData<RecentBrowserContent> => {
  const [_recentData, setRecentData] = useState<
    RecentData<RecentBrowserContent>
  >({
    content: [],
    isLoading: true,
  });

  useEffect(() => {
    const sub = recentBrowserHistory().subscribe(setRecentData);
    return () => {
      sub.unsubscribe();
    };
  }, []);

  return _recentData;
};

export const useRecentEntities = (): RecentData<RecentConnectorContent> => {
  const [_recentData, setRecentData] = useState<
    RecentData<RecentConnectorContent>
  >({
    content: [],
    isLoading: true,
  });

  useEffect(() => {
    const sub = recentEntities().subscribe(setRecentData);

    return () => {
      sub.unsubscribe();
    };
  }, []);

  return _recentData;
};

export const useRecentActivities = (): RecentData<RecentActivitiesContent> => {
  const [_recentData, setRecentData] = useState<
    RecentData<RecentActivitiesContent>
  >({
    content: [],
    isLoading: true,
  });

  useEffect(() => {
    const sub = recentActivities().subscribe(setRecentData);
    return () => {
      sub.unsubscribe();
    };
  }, []);

  return _recentData;
};

const DISMIISED_RECENTS_EXTENSION_UPSELL = "dismissedRecentsExtensionUpsell";
export const useRecentExtensionUpsell = (): RecentData<RecentUpsellContent> => {
  const [_recentData, setRecentData] = useState<
    RecentData<RecentUpsellContent>
  >({
    content: [],
    isLoading: true,
  });
  const [dismissed, setDismissed] = useState(true);
  const [dismissedDataLoaded, setDismissedDataLoaded] = useState(false);

  const { extensionInfo, isLoading } = useExtensionUpsell(
    "dash_2025_01_29_extension_upsell_on_recents",
  );
  const { getOnboardingValue, setOnboardingValue } = useOnboardingValues();

  useEffect(() => {
    const getDismissed = async () => {
      const isDismissed = await getOnboardingValue(
        DISMIISED_RECENTS_EXTENSION_UPSELL,
      );
      setDismissed(isDismissed);
      setDismissedDataLoaded(true);
    };
    getDismissed();
  }, [getOnboardingValue]);

  useEffect(() => {
    if (isLoading || !dismissedDataLoaded) return;

    if (!dismissed && extensionInfo) {
      setRecentData({
        content: [
          {
            url: extensionInfo.installUrl,
            // make timestamp as future time so it shows up at the top
            visit_ms: Date.now() + 1000000,
            uuid: extensionInfo.browserName,
            title: i18n.t("get_browser_extension", {
              browserName: extensionInfo.browserName,
            }),
            icon: extensionInfo.icon,
            connectorInfo: {
              type: "upsell",
              displayName: i18n.t("get_extension"),
              connectorName: "extension_upsell",
            },
            onDismiss: () => {
              setOnboardingValue(DISMIISED_RECENTS_EXTENSION_UPSELL, true);
              setDismissed(true);
            },
          },
        ],
        isLoading: false,
      });
    } else {
      setRecentData({
        content: [],
        isLoading: false,
      });
    }
  }, [extensionInfo, isLoading, dismissed, dismissedDataLoaded]);

  return _recentData;
};
